/**
 * Export React Utils - Entry point for React component export
 *
 * Generates a ZIP containing standalone TSX component files
 * that can be dropped into any React project (CRA, Vite, Next.js).
 */

import { BlobWriter, ZipWriter, TextReader, BlobReader } from "@zip.js/zip.js";
import { ThemeSettings } from "../types";
import { generateTsxFiles } from "./reactExport/tsxCodeGenerator";

// Default theme if none provided
const defaultTheme: ThemeSettings = {
  primary: "#3B82F6",
  secondary: "#10B981",
  font: "Sarabun",
};

/**
 * Fetch an image as a Blob
 */
const fetchImageAsBlob = async (url: string): Promise<Blob | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await response.blob();
  } catch {
    console.warn(`Failed to fetch image: ${url}`);
    return null;
  }
};

/**
 * Convert a slug to PascalCase for the folder name
 */
const toPascalCase = (s: string): string => {
  return s
    .replace(/[^a-zA-Z0-9]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("");
};

/**
 * Export a single page as a React component ZIP
 *
 * @param content - CraftJS JSON content (string or parsed object)
 * @param slug - Page slug (used for folder name and CSS scoping)
 * @param theme - Theme settings (colors, font)
 * @returns ZIP blob ready for download
 */
export const exportPageToReactZip = async (
  content: string | Record<string, unknown>,
  slug: string,
  theme?: ThemeSettings,
): Promise<Blob> => {
  const usedTheme = theme || defaultTheme;
  const folderName = toPascalCase(slug) || "Page";

  // Generate TSX files
  const { files, images } = generateTsxFiles(content, slug, usedTheme);

  // Create ZIP
  const blobWriter = new BlobWriter("application/zip");
  const zipWriter = new ZipWriter(blobWriter);

  // Add all generated text files
  for (const [path, fileContent] of files) {
    await zipWriter.add(`${folderName}/${path}`, new TextReader(fileContent));
  }

  // Fetch and add local images to assets/
  for (const image of images) {
    if (image.isLocal) {
      // Ensure URL starts with / for fetch
      const fetchUrl = image.originalUrl.startsWith("/")
        ? image.originalUrl
        : `/${image.originalUrl}`;

      const blob = await fetchImageAsBlob(fetchUrl);
      if (blob) {
        await zipWriter.add(`${folderName}/assets/${image.filename}`, new BlobReader(blob));
      } else {
        console.warn(`Could not fetch image: ${fetchUrl}`);
      }
    }
  }

  // Close and return
  const zipBlob = await zipWriter.close();
  return zipBlob;
};
